import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bell,
  Building2,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Clock3,
  Database,
  Layers3,
  Newspaper,
  Package,
  Percent,
  Search,
  Settings,
  Shield,
  Smartphone,
  UserRound,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import Dropdown from '../../components/Dropdown';
import FloatingField from '../../components/FloatingField';
import ModalShell from '../../components/ModalShell';
import { CompactListSkeleton } from '../../components/Skeleton';
import useToast from '../../hooks/useToast';
import * as activityApi from '../../services/activityApi';
import {
  getActivityFieldLabel,
  stringifyLocalizedActivityData,
  translateActivityString,
} from '../../utils/activityLocalization';
import { formatTimezoneDate } from '../../utils/timezone';
import { haptic } from '../../utils/vibration';
import '../../styles/Management.css';

const EMPTY_META = { users: [], actions: [], categories: [], entities: [] };
const ALL_USERS_VALUE = '__all_users__';
const ALL_CATEGORIES_VALUE = '__all_categories__';
const DEFAULT_ACTIVITY_WINDOW_DAYS = 30;
const MAX_ACTIVITY_WINDOW_DAYS = 60;
const ACTIVITY_PAGE_SIZE = 40;
const ORDER_RELATED_ENTITY_TYPES = new Set([
  'order',
  'order_item',
  'order_stage',
  'order_comment',
  'order_photo',
  'order_invoice',
  'order_revision',
  'stage',
]);

const CATEGORY_STYLES = {
  auth: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/30 dark:text-sky-200 dark:ring-sky-900/60',
  users: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/30 dark:text-violet-200 dark:ring-violet-900/60',
  clients: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-900/60',
  orders: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:ring-blue-900/60',
  services: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:ring-emerald-900/60',
  departments: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-200 dark:ring-cyan-900/60',
  discounts: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-950/30 dark:text-fuchsia-200 dark:ring-fuchsia-900/60',
  tasks: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-950/30 dark:text-orange-200 dark:ring-orange-900/60',
  finance: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/30 dark:text-rose-200 dark:ring-rose-900/60',
  news: 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-200 dark:ring-indigo-900/60',
  notifications: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/30 dark:text-teal-200 dark:ring-teal-900/60',
  settings: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700',
  push: 'bg-lime-50 text-lime-700 ring-lime-200 dark:bg-lime-950/30 dark:text-lime-200 dark:ring-lime-900/60',
  system: 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700',
};

const CATEGORY_ICONS = {
  auth: Shield,
  users: Users,
  clients: UserRound,
  orders: Package,
  services: Layers3,
  departments: Building2,
  discounts: Percent,
  tasks: ClipboardList,
  finance: Wallet,
  news: Newspaper,
  notifications: Bell,
  settings: Settings,
  push: Smartphone,
  system: Database,
};

const VALUE_LABELS = {
  active: 'Активен',
  blocked: 'Заблокирован',
  deleted: 'Удален',
  draft: 'Черновик',
  scheduled: 'Запланировано',
  expired: 'Истекло',
  archived: 'В архиве',
  admin: 'Администратор',
  developer: 'Разработчик',
  dispatcher: 'Диспетчер',
  manager: 'Менеджер',
  operator: 'Оператор',
  courier: 'Курьер',
  b2c: 'Физлица',
  b2b: 'Юрлица',
  all: 'Все',
  news: 'Новость',
  instruction: 'Инструкция',
  laundry: 'Прачечная',
  cleaning: 'Химчистка',
  washing: 'Стирка',
  pickup: 'Забор',
  delivery: 'Доставка',
  handover: 'Выдача',
  approval: 'Согласование',
  drying_packing: 'Сушка и упаковка',
  pending: 'Ожидает',
  in_progress: 'В работе',
  completed: 'Завершено',
  cancelled: 'Отменено',
  skipped: 'Пропущено',
  available: 'Доступно',
  assigned: 'Назначено',
  company: 'Компания',
  individual: 'Физлицо',
  company_bank: 'Расчетный счет',
  employee: 'Сотрудник',
  employee_wallet: 'Кошелек сотрудника',
  cash: 'Наличные',
  bank: 'Банк',
  email: 'Электронная почта',
  paper: 'Бумажный',
  self_pickup: 'Самовывоз',
  self_delivery: 'Без выезда',
  normal: 'Обычный',
  urgent: 'Срочный',
  high: 'Высокий',
  low: 'Низкий',
  rub: 'Рубли',
  percent: 'Проценты',
  info: 'Информационное',
  warning: 'Предупреждение',
  error: 'Ошибка',
  success: 'Успешно',
  paid: 'Оплачено',
};

const FIELD_LABELS = {
  login: 'Логин',
  first_name: 'Имя',
  last_name: 'Фамилия',
  middle_name: 'Отчество',
  company_name: 'Компания',
  full_name: 'Полное имя',
  phone: 'Телефон',
  active: 'Активность',
  vip: 'Особый клиент',
  undesirable: 'Нежелательный клиент',
  title: 'Заголовок',
  category: 'Категория',
  target_summary: 'Показывается',
  title_image_url: 'Изображение',
  content_preview: 'Текст',
  description: 'Описание',
  amount: 'Сумма',
  tag: 'Тег',
  transaction_type: 'Тип операции',
  wallet_scope: 'Кошелек',
  payment_status: 'Оплата',
  payment_method: 'Способ оплаты',
  key: 'Ключ',
  value: 'Значение',
  departments: 'Подразделения',
  order_status: 'Статус заказа',
  status: 'Статус',
  stage_status: 'Статус этапа',
  stage_label: 'Этап',
  stage: 'Этап',
  stage_name: 'Этап',
  reason: 'Причина',
  notification_type: 'Тип уведомления',
  target_user_name: 'Кому',
  user_name: 'Пользователь',
  user_type: 'Роль',
  service_type: 'Тип услуги',
  client_type: 'Тип клиента',
  processing_route: 'Маршрут обработки',
  unit_type: 'Тип цены',
  requires_approval: 'Нужно подтверждение',
  order_number: 'Номер заказа',
  task_number: 'Номер поручения',
  invoice_number: 'Номер счета',
  invoice_date: 'Дата счета',
  invoice_delivery_method: 'Способ передачи счета',
  invoice_prepared_at: 'Счет подготовлен',
  dispatcher_priority: 'Приоритет',
  dispatcher_call_required_stage: 'Признак созвона',
  rows_count: 'Строк в документе',
  items_count: 'Количество позиций',
  quantity: 'Количество',
  price: 'Цена',
  subtotal: 'Сумма',
  discount_amount: 'Скидка',
  final_amount: 'Итого',
  prepayment: 'Предоплата',
  remaining_amount: 'Остаток',
  text: 'Описание',
  content: 'Содержимое',
  name: 'Название',
  comment: 'Комментарий',
  note: 'Заметка',
  notes: 'Заметки',
  internal_notes: 'Внутренняя заметка',
  initial_snapshot: 'Состав при создании',
  assignee_name: 'Исполнитель',
  department_name: 'Подразделение',
  old_status: 'Предыдущий статус',
  new_status: 'Новый статус',
};

const STAT_CARDS = [
  { key: 'total', label: 'Записей' },
  { key: 'users', label: 'Людей' },
  { key: 'categories', label: 'Разделов' },
  { key: 'actions', label: 'Действий' },
];

const getCategoryStyle = (category) => CATEGORY_STYLES[category] || CATEGORY_STYLES.system;
const getCategoryIcon = (category) => CATEGORY_ICONS[category] || Database;
const normalizeLookup = (value) => String(value || '').trim().toLowerCase();

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const shiftDateInput = (value, days) => {
  const date = parseDateInput(value);
  if (!date) return value;
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const getDefaultActivityWindow = () => {
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - DEFAULT_ACTIVITY_WINDOW_DAYS + 1);

  return {
    date_from: toDateInputValue(from),
    date_to: toDateInputValue(today),
  };
};

const createInitialFilters = () => {
  const params = new URLSearchParams(window.location.search);
  const defaultWindow = getDefaultActivityWindow();

  return {
    user_id: params.get('user_id') || '',
    query: '',
    category: '',
    date_from: params.get('date_from') || defaultWindow.date_from,
    date_to: params.get('date_to') || defaultWindow.date_to,
    limit: ACTIVITY_PAGE_SIZE,
    offset: 0,
  };
};

const getComparableFilters = (filters) => JSON.stringify({
  user_id: filters.user_id || '',
  query: String(filters.query || '').trim(),
  category: filters.category || '',
  date_from: filters.date_from || '',
  date_to: filters.date_to || '',
  limit: filters.limit || 100,
});

const normalizeDateRange = (filters, changedKey = null) => {
  if (!filters.date_from || !filters.date_to) return filters;

  const nextFilters = { ...filters };
  const fromDate = parseDateInput(nextFilters.date_from);
  const toDate = parseDateInput(nextFilters.date_to);
  if (!fromDate || !toDate) return filters;

  if (fromDate > toDate) {
    if (changedKey === 'date_from') {
      nextFilters.date_to = nextFilters.date_from;
    } else if (changedKey === 'date_to') {
      nextFilters.date_from = nextFilters.date_to;
    }
  }

  const normalizedFrom = parseDateInput(nextFilters.date_from);
  const normalizedTo = parseDateInput(nextFilters.date_to);
  if (!normalizedFrom || !normalizedTo) return nextFilters;

  const diffDays = Math.floor((normalizedTo.getTime() - normalizedFrom.getTime()) / 86400000) + 1;
  if (diffDays <= MAX_ACTIVITY_WINDOW_DAYS) return nextFilters;

  if (changedKey === 'date_from' || changedKey === null) {
    nextFilters.date_to = shiftDateInput(nextFilters.date_from, MAX_ACTIVITY_WINDOW_DAYS - 1);
    return nextFilters;
  }

  nextFilters.date_from = shiftDateInput(nextFilters.date_to, -(MAX_ACTIVITY_WINDOW_DAYS - 1));
  return nextFilters;
};

const safeStringify = (value) => {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const humanizeKey = (key) => FIELD_LABELS[key] || getActivityFieldLabel(key);

const translateStringValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  const exactMatch = VALUE_LABELS[normalizeLookup(normalized)];
  if (exactMatch) return exactMatch;
  return translateActivityString(normalized);
};

const formatSummaryText = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Без описания';

  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) return translateStringValue(text);

  const left = text.slice(0, colonIndex).trim();
  const right = text.slice(colonIndex + 1).trim();
  return right
    ? `${translateStringValue(left)}: ${translateStringValue(right)}`
    : translateStringValue(left);
};

const formatValue = (value, key = '') => {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'initial_snapshot' && isPlainObject(value)) {
    const lines = [];
    if (Array.isArray(value.items) && value.items.length > 0) {
      lines.push(`Предметы (${value.items.length}):`);
      value.items.forEach((item, idx) => {
        const num = item.item_number ? `№${item.item_number} ` : (item.fallback_number ? `#${item.fallback_number} ` : '');
        const qty = item.quantity ? ` · ${item.quantity} ${item.unit || 'шт'}` : '';
        const price = item.subtotal !== undefined ? ` · ${item.subtotal} ₽` : '';
        lines.push(`  ${idx + 1}. ${num}${item.item_name || 'Предмет'}${qty}${price}`);
      });
    }
    if (Array.isArray(value.services) && value.services.length > 0) {
      lines.push(`Услуги (${value.services.length}):`);
      value.services.forEach((s) => {
        lines.push(`  + ${s.item_name || 'Услуга'}${s.subtotal ? ` · ${s.subtotal} ₽` : ''}`);
      });
    }
    if (Array.isArray(value.discounts) && value.discounts.length > 0) {
      lines.push(`Скидки (${value.discounts.length}):`);
      value.discounts.forEach((d) => {
        lines.push(`  - ${d.item_name || 'Скидка'}${d.subtotal ? ` · ${d.subtotal} ₽` : ''}`);
      });
    }
    if (value.totals && value.totals.final_amount !== undefined) {
      lines.push(`Итого: ${value.totals.final_amount} ₽`);
    }
    if (lines.length > 0) {
      return lines.join('\n');
    }
    return value.text || 'Без предметов (заявка на забор)';
  }
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return translateStringValue(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value.map((item) => formatValue(item, key)).join(', ');
  }
  if (isPlainObject(value)) return stringifyLocalizedActivityData(value);
  return String(value);
};

const buildChangeRows = (oldValue, newValue) => {
  if (!isPlainObject(oldValue) && !isPlainObject(newValue)) return [];

  const before = isPlainObject(oldValue) ? oldValue : {};
  const after = isPlainObject(newValue) ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return keys
    .filter((key) => safeStringify(before[key]) !== safeStringify(after[key]))
    .slice(0, 24)
    .map((key) => ({
      key,
      label: humanizeKey(key),
      oldValue: before[key],
      newValue: after[key],
    }));
};

const hasDetails = (item) => item?.old_value_data != null || item?.new_value_data != null;
const isOrderRelatedEntity = (item) => ORDER_RELATED_ENTITY_TYPES.has(normalizeLookup(item?.entity_type));
const getOrderDisplayLabel = (item) => {
  const orderNumber = String(item?.order_number || '').trim();
  if (orderNumber) return `Заказ №${orderNumber}`;
  return item?.order_id ? `Заказ #${item.order_id}` : '';
};
const getEntityDisplayLabel = (item) => {
  const baseLabel = translateStringValue(item?.entity_label || 'Система');
  if (!isOrderRelatedEntity(item)) {
    return item?.entity_id ? `${baseLabel} #${item.entity_id}` : baseLabel;
  }

  const orderLabel = getOrderDisplayLabel(item);
  if (normalizeLookup(item?.entity_type) === 'order') {
    return orderLabel || baseLabel;
  }
  return orderLabel ? `${baseLabel} · ${orderLabel}` : baseLabel;
};

const ActivityLog = () => {
  const userType = localStorage.getItem('user_type');
  const { toast, showToast, hideToast } = useToast(3200);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(EMPTY_META);
  const [selectedLog, setSelectedLog] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => normalizeDateRange({
    ...createInitialFilters(),
    limit: ACTIVITY_PAGE_SIZE,
  }, 'date_to'));
  const [draftFilters, setDraftFilters] = useState(() => normalizeDateRange({
    ...createInitialFilters(),
    limit: ACTIVITY_PAGE_SIZE,
  }, 'date_to'));
  const loadMoreTriggerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        setLoading(true);
        setLoadingMore(false);
        setReachedEnd(false);
        const pageSize = appliedFilters.limit || ACTIVITY_PAGE_SIZE;
        const baseFilters = {
          ...appliedFilters,
          limit: pageSize,
          offset: 0,
        };

        const [logsData, statsData, metaData] = await Promise.all([
          activityApi.getActivityLogs(baseFilters),
          activityApi.getActivityStats(appliedFilters),
          activityApi.getActivityMeta({
            query: appliedFilters.query,
            category: appliedFilters.category,
            date_from: appliedFilters.date_from,
            date_to: appliedFilters.date_to,
          }),
        ]);

        if (cancelled) return;
        const nextLogs = Array.isArray(logsData) ? logsData : [];
        const totalLogs = Number(statsData?.total || 0);
        setLogs(nextLogs);
        setStats(statsData || null);
        setMeta(metaData || EMPTY_META);
        setReachedEnd(nextLogs.length < pageSize || (totalLogs > 0 && nextLogs.length >= totalLogs));
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка загрузки активности:', err);
        showToast(err.message || 'Не удалось загрузить журнал активности', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const setDraftFilter = (key, value) => {
    setDraftFilters((prev) => normalizeDateRange({ ...prev, [key]: value, offset: 0 }, key));
  };

  const resetDraftFilters = () => {
    setDraftFilters(normalizeDateRange({
      ...createInitialFilters(),
      limit: ACTIVITY_PAGE_SIZE,
    }, 'date_to'));
  };

  const applyDatePreset = (days) => {
    haptic('selection');
    const safeDays = Math.min(days, MAX_ACTIVITY_WINDOW_DAYS);
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - safeDays + 1);
    setDraftFilters((prev) => ({
      ...prev,
      date_from: toDateInputValue(from),
      date_to: toDateInputValue(today),
      offset: 0,
    }));
  };

  const applyFilters = () => {
    haptic('selection');
    const normalizedFilters = normalizeDateRange({ ...draftFilters, offset: 0 }, null);
    setDraftFilters(normalizedFilters);
    setAppliedFilters(normalizedFilters);
    setSelectedLog(null);
  };

  const formatDate = (value) => formatTimezoneDate(value, true);
  const getUserLabel = (item) => item?.user_name || `Пользователь #${item?.user_id}`;
  const getTargetLabel = (item) => translateStringValue(item?.target_name || '');

  const userOptions = [
    { value: ALL_USERS_VALUE, label: 'Все пользователи', icon: Users, variant: 'neutral' },
    ...meta.users.map((user) => ({
      value: String(user.id),
      label: `${user.label} (${user.count})`,
      icon: UserRound,
    })),
  ];

  const categoryOptions = [
    { value: ALL_CATEGORIES_VALUE, label: 'Все разделы', icon: Layers3, variant: 'neutral' },
    ...meta.categories.map((category) => ({
      value: category.key,
      label: `${category.label} (${category.count})`,
      icon: getCategoryIcon(category.key),
    })),
  ];

  const activeChips = [];
  if (appliedFilters.query) activeChips.push({ key: 'query', label: `Поиск: ${appliedFilters.query}` });
  if (appliedFilters.user_id) {
    const userLabel = meta.users.find((item) => String(item.id) === String(appliedFilters.user_id))?.label || `Пользователь #${appliedFilters.user_id}`;
    activeChips.push({ key: 'user_id', label: `Пользователь: ${userLabel}` });
  }
  if (appliedFilters.category) {
    const categoryLabel = meta.categories.find((item) => item.key === appliedFilters.category)?.label || appliedFilters.category;
    activeChips.push({ key: 'category', label: `Раздел: ${categoryLabel}` });
  }
  const defaultActivityWindow = getDefaultActivityWindow();
  const hasCustomDateRange =
    appliedFilters.date_from !== defaultActivityWindow.date_from || appliedFilters.date_to !== defaultActivityWindow.date_to;
  if (hasCustomDateRange) {
    activeChips.push({
      key: 'date_range',
      label: `Период: ${appliedFilters.date_from || '...'} - ${appliedFilters.date_to || '...'}`,
    });
  }

  const summaryValues = {
    total: stats?.total || 0,
    users: Object.keys(stats?.by_user || {}).length,
    categories: Object.keys(stats?.by_category || {}).length,
    actions: Object.keys(stats?.by_action || {}).length,
  };

  const hasPendingChanges = getComparableFilters(draftFilters) !== getComparableFilters(appliedFilters);
  const hasMoreLogs = !reachedEnd && logs.length > 0 && summaryValues.total > logs.length;

  const selectedDiffRows = buildChangeRows(selectedLog?.old_value_data, selectedLog?.new_value_data);

  const loadMoreLogs = useCallback(async () => {
    if (loading || loadingMore || !hasMoreLogs) return;

    const pageSize = appliedFilters.limit || ACTIVITY_PAGE_SIZE;
    try {
      setLoadingMore(true);
      const nextOffset = logs.length;
      const nextLogsData = await activityApi.getActivityLogs({
        ...appliedFilters,
        limit: pageSize,
        offset: nextOffset,
      });
      const nextLogs = Array.isArray(nextLogsData) ? nextLogsData : [];

      setLogs((prev) => [...prev, ...nextLogs]);
      if (nextLogs.length < pageSize || (summaryValues.total > 0 && nextOffset + nextLogs.length >= summaryValues.total)) {
        setReachedEnd(true);
      }
    } catch (err) {
      console.error('Ошибка догрузки активности:', err);
      showToast(err.message || 'Не удалось догрузить журнал активности', 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [appliedFilters, hasMoreLogs, loading, loadingMore, logs.length, showToast, summaryValues.total]);

  useEffect(() => {
    if (!hasMoreLogs || loading || loadingMore) return undefined;

    const node = loadMoreTriggerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMoreLogs();
      }
    }, {
      rootMargin: '280px 0px',
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreLogs, loadMoreLogs, loading, loadingMore]);

  if (userType !== 'developer') {
    return <Navigate to="/management" replace />;
  }

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      <div className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
        <div className="space-y-4 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">Активность</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Журнал изменений по людям и разделам без лишнего шума.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              {STAT_CARDS.map((card) => (
                <div
                  key={card.key}
                  className="min-w-[86px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/60"
                >
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">{card.label}</div>
                  <div className="mt-0.5 text-sm font-semibold text-gray-950 dark:text-white">{summaryValues[card.key]}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-12">
            <div className="xl:col-span-5">
              <FloatingField label="Поиск" labelClassName="!left-10">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={draftFilters.query}
                    onChange={(event) => setDraftFilter('query', event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        applyFilters();
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-blue-500"
                    placeholder="Люди, объекты, номера, текст изменений"
                  />
                </div>
              </FloatingField>
            </div>

            <div className="xl:col-span-3">
              <Dropdown
                label="Пользователь"
                value={draftFilters.user_id || ALL_USERS_VALUE}
                options={userOptions}
                onChange={(value) => setDraftFilter('user_id', value === ALL_USERS_VALUE ? '' : value)}
                menuLabel="Пользователь"
                buttonClassName="rounded-lg py-2.5"
                menuClassName="rounded-lg"
                itemClassName="py-2.5"
              />
            </div>

            <div className="xl:col-span-2">
              <Dropdown
                label="Раздел"
                value={draftFilters.category || ALL_CATEGORIES_VALUE}
                options={categoryOptions}
                onChange={(value) => setDraftFilter('category', value === ALL_CATEGORIES_VALUE ? '' : value)}
                menuLabel="Раздел"
                buttonClassName="rounded-lg py-2.5"
                menuClassName="rounded-lg"
                itemClassName="py-2.5"
              />
            </div>

            <div className="xl:col-span-1">
              <FloatingField label="С">
                <input
                  type="date"
                  value={draftFilters.date_from}
                  onChange={(event) => setDraftFilter('date_from', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </FloatingField>
            </div>

            <div className="xl:col-span-1">
              <FloatingField label="По">
                <input
                  type="date"
                  value={draftFilters.date_to}
                  onChange={(event) => setDraftFilter('date_to', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </FloatingField>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
            <button
              type="button"
              onClick={() => applyDatePreset(1)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Сегодня
            </button>
            <button
              type="button"
              onClick={() => applyDatePreset(7)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              7 дней
            </button>
            <button
              type="button"
              onClick={() => applyDatePreset(30)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              30 дней
            </button>
            <button
              type="button"
              onClick={() => applyDatePreset(60)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              60 дней
            </button>
            <button
              type="button"
              onClick={applyFilters}
              disabled={!hasPendingChanges || loading}
              className={`ml-auto rounded-lg px-3 py-2 text-xs font-medium transition ${
                hasPendingChanges && !loading
                  ? 'border border-gray-900 bg-gray-900 text-white hover:bg-gray-800 dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200'
                  : 'border border-gray-300 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500'
              }`}
            >
              Найти
            </button>
            <button
              type="button"
              onClick={resetDraftFilters}
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Сбросить
            </button>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            По умолчанию показываем последние {DEFAULT_ACTIVITY_WINDOW_DAYS} дней. Максимальный период: {MAX_ACTIVITY_WINDOW_DAYS} дней.
          </div>

          {hasPendingChanges && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              Фильтры изменены, но еще не применены. Нажмите «Найти».
            </div>
          )}

          {meta.categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDraftFilter('category', '')}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                  draftFilters.category
                    ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                    : 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                }`}
              >
                <Layers3 className="h-4 w-4" />
                Все разделы
              </button>
              {meta.categories.map((category) => {
                const Icon = getCategoryIcon(category.key);
                return (
                  <button
                    key={category.key}
                    type="button"
                    onClick={() => setDraftFilter('category', category.key)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${
                      draftFilters.category === category.key
                        ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                        : `${getCategoryStyle(category.key)}`
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => {
                if (chip.key === 'date_range') {
                  const normalizedFilters = normalizeDateRange(
                    { ...appliedFilters, ...getDefaultActivityWindow(), offset: 0 },
                    'date_to',
                  );
                  setDraftFilters(normalizedFilters);
                  setAppliedFilters(normalizedFilters);
                  return;
                }
                const normalizedFilters = normalizeDateRange(
                  { ...appliedFilters, [chip.key]: '', offset: 0 },
                  chip.key,
                );
                setDraftFilters(normalizedFilters);
                setAppliedFilters(normalizedFilters);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {chip.label}
              <X className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
        {loading ? (
          <div className="p-3">
            <CompactListSkeleton count={6} />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
              <Search className="h-5 w-5" />
            </div>
            <div className="mt-3 text-sm font-medium text-gray-900 dark:text-white">Записей не найдено</div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Попробуйте убрать часть фильтров или расширить период.</div>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {logs.map((item) => {
                const Icon = getCategoryIcon(item.category);
                const targetLabel = getTargetLabel(item);

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      haptic('open');
                      setSelectedLog(item);
                    }}
                    className="w-full px-3 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-gray-800/70"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${getCategoryStyle(item.category)}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-gray-500 dark:text-gray-400">
                              <span className="font-medium text-gray-950 dark:text-white">{getUserLabel(item)}</span>
                              <span>{translateStringValue(item.action_label)}</span>
                              {item.entity_label ? (
                                <>
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <span>{translateStringValue(item.entity_label)}</span>
                                </>
                              ) : null}
                            </div>
                            <div className="mt-1 line-clamp-2 text-sm text-gray-900 dark:text-gray-100">
                              {formatSummaryText(item.summary)}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <div className="hidden text-right sm:block">
                              <div className="text-[11px] font-medium text-gray-700 dark:text-gray-200">{formatDate(item.created_date)}</div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                          <span className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300">
                            <Clock3 className="h-3.5 w-3.5" />
                            {formatDate(item.created_date)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300">
                            {React.createElement(Icon, { className: 'h-3.5 w-3.5' })}
                            {translateStringValue(item.category_label)}
                          </span>
                          {targetLabel ? (
                            <span className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300">
                              {targetLabel}
                            </span>
                          ) : null}
                          {getOrderDisplayLabel(item) ? (
                            <span className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300">
                              {getOrderDisplayLabel(item)}
                            </span>
                          ) : null}
                          {hasDetails(item) ? (
                            <span className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300">
                              Есть детали
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {(hasMoreLogs || loadingMore) ? (
              <div ref={loadMoreTriggerRef} className="border-t border-gray-100 px-4 py-4 dark:border-gray-800">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Показано {logs.length} из {summaryValues.total}
                  </div>
                  {loadingMore ? (
                    <CompactListSkeleton count={2} className="w-full text-left" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('tap');
                        loadMoreLogs();
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      Показать еще
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </>
      )}
            </div>

      <ModalShell
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        variant="sheet"
        panelClassName="max-h-[90vh] overflow-hidden rounded-t-xl sm:mb-6 sm:max-w-3xl sm:rounded-xl"
      >
        {selectedLog && (
          <div className="flex max-h-[90vh] flex-col">
            <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-1 text-xs ring-1 ${getCategoryStyle(selectedLog.category)}`}>
                      {React.createElement(getCategoryIcon(selectedLog.category), { className: 'h-4 w-4' })}
                      {translateStringValue(selectedLog.category_label)}
                    </span>
                    <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                      {translateStringValue(selectedLog.action_label)}
                    </span>
                  </div>
                  <div className="mt-2 text-base font-semibold text-gray-950 dark:text-white">
                    {formatSummaryText(selectedLog.summary)}
                  </div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {getUserLabel(selectedLog)} • {formatDate(selectedLog.created_date)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="rounded-lg bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 overflow-y-auto px-4 py-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Раздел</div>
                  <div className="mt-1 font-medium text-gray-900 dark:text-white">
                    {translateStringValue(selectedLog.category_label)}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Сущность</div>
                  <div className="mt-1 font-medium text-gray-900 dark:text-white">
                    {getEntityDisplayLabel(selectedLog)}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Кто сделал</div>
                  <div className="mt-1 font-medium text-gray-900 dark:text-white">
                    {getUserLabel(selectedLog)}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Когда</div>
                  <div className="mt-1 font-medium text-gray-900 dark:text-white">{formatDate(selectedLog.created_date)}</div>
                </div>
              </div>

              {selectedDiffRows.length > 0 && (
                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Что изменилось</div>
                  <div className="mt-3 space-y-3">
                    {selectedDiffRows.map((row) => (
                      <div key={row.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <div className="rounded-lg bg-white px-3 py-2 text-sm text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                            <div className="mb-1 text-[11px] text-gray-400">Было</div>
                            <div className="whitespace-pre-wrap break-words">{formatValue(row.oldValue, row.key)}</div>
                          </div>
                          <div className="rounded-lg bg-white px-3 py-2 text-sm text-gray-900 dark:bg-gray-900 dark:text-white">
                            <div className="mb-1 text-[11px] text-gray-400">Стало</div>
                            <div className="whitespace-pre-wrap break-words">{formatValue(row.newValue, row.key)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <details className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
                <summary className="cursor-pointer list-none text-sm font-semibold text-gray-900 dark:text-white">
                  Дополнительно
                </summary>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/50">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Объект</div>
                    <div className="mt-1 whitespace-pre-wrap break-words text-gray-900 dark:text-white">
                      {getTargetLabel(selectedLog) || '—'}
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/50">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Заказ</div>
                    <div className="mt-1 text-gray-900 dark:text-white">{getOrderDisplayLabel(selectedLog) || '—'}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/50">
                    <div className="text-xs text-gray-500 dark:text-gray-400">IP-адрес</div>
                    <div className="mt-1 text-gray-900 dark:text-white">{selectedLog.ip_address || '—'}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/50">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Данные браузера</div>
                    <div className="mt-1 whitespace-pre-wrap break-words text-gray-900 dark:text-white">
                      {selectedLog.user_agent || '—'}
                    </div>
                  </div>
                </div>
              </details>

              <details className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
                <summary className="cursor-pointer list-none text-sm font-semibold text-gray-900 dark:text-white">
                  Исходные данные до изменения
                </summary>
                <pre className="mt-3 overflow-x-auto rounded-lg bg-gray-950 p-3 text-xs text-gray-100">
                  {stringifyLocalizedActivityData(selectedLog.old_value_data ?? selectedLog.old_value ?? '—')}
                </pre>
              </details>

              <details className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
                <summary className="cursor-pointer list-none text-sm font-semibold text-gray-900 dark:text-white">
                  Исходные данные после изменения
                </summary>
                <pre className="mt-3 overflow-x-auto rounded-lg bg-gray-950 p-3 text-xs text-gray-100">
                  {stringifyLocalizedActivityData(selectedLog.new_value_data ?? selectedLog.new_value ?? '—')}
                </pre>
              </details>
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default ActivityLog;
